import { useCallback, useEffect, useSyncExternalStore } from 'react'
import type { CurrencyCode } from '@/lib/money'

/**
 * Onboarding is one ordered flow, and this is its only source of truth. Screens
 * record what was chosen and which step comes next; the route guards read the
 * same record to decide where a person belongs. Nothing navigates on its own
 * after an auth change, so there is no race between a screen and a guard.
 */
export type OnboardingStep =
  'create' | 'verify' | 'code' | 'identity' | 'accounts' | 'card' | 'ready'

export const STEP_PATH: Record<OnboardingStep, string> = {
  create: '/onboarding/create',
  verify: '/onboarding/verify',
  code: '/onboarding/verify/code',
  identity: '/onboarding/identity',
  accounts: '/onboarding/accounts',
  card: '/onboarding/card',
  ready: '/onboarding/ready',
}

/**
 * The five numbered steps the design shows. Registration charges nothing:
 * accounts and cards are created here and paid for when they are activated.
 */
export const STEP_NUMBER: Record<OnboardingStep, number> = {
  create: 1,
  verify: 2,
  code: 2,
  identity: 3,
  accounts: 4,
  card: 5,
  ready: 5,
}

export const TOTAL_STEPS = 5

export type VerifyChannel = 'whatsapp' | 'sms' | 'email'
export type CardChoice = 'virtual' | 'physical' | 'both'

export interface OnboardingDraft {
  readonly step: OnboardingStep
  readonly name: string
  readonly email: string
  readonly phone: string
  readonly channel: VerifyChannel
  readonly accounts: readonly CurrencyCode[]
  readonly card: CardChoice
}

const KEY = 'pass.onboarding'

const EMPTY: OnboardingDraft = {
  step: 'create',
  name: '',
  email: '',
  phone: '',
  channel: 'whatsapp',
  accounts: ['USD', 'MXN'],
  card: 'virtual',
}

let cached: OnboardingDraft | null = null
const listeners = new Set<() => void>()

/**
 * Drafts saved before registration stopped charging may still point at the
 * old payment steps; they resume on the step that now follows each one.
 */
const RETIRED_STEPS: Record<string, OnboardingStep> = {
  'accounts-pay': 'card',
  'card-pay': 'ready',
}

function load(): OnboardingDraft {
  if (cached) return cached
  try {
    const raw = window.localStorage.getItem(KEY)
    const saved = raw ? (JSON.parse(raw) as Partial<OnboardingDraft> & { step?: string }) : {}
    const step = !saved.step
      ? undefined
      : saved.step in STEP_PATH
        ? (saved.step as OnboardingStep)
        : RETIRED_STEPS[saved.step]
    const { accounts, card, channel, email, name, phone } = { ...EMPTY, ...saved }
    cached = { ...EMPTY, accounts, card, channel, email, name, phone, ...(step ? { step } : {}) }
  } catch {
    cached = EMPTY
  }
  return cached
}

function persist(next: OnboardingDraft): void {
  cached = next
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    /* private mode — the draft lives for this page only */
  }
  for (const listener of listeners) listener()
}

export function readDraft(): OnboardingDraft {
  return load()
}

export function saveDraft(patch: Partial<OnboardingDraft>): OnboardingDraft {
  const next = { ...load(), ...patch }
  persist(next)
  return next
}

export function resetDraft(patch: Partial<OnboardingDraft> = {}): void {
  persist({ ...EMPTY, ...patch })
}

/** Where someone who has not finished onboarding should be right now. */
export function resumePath(): string {
  return STEP_PATH[load().step]
}

export function useOnboardingDraft(): readonly [
  OnboardingDraft,
  (patch: Partial<OnboardingDraft>) => void,
] {
  const draft = useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    load,
    load,
  )
  const update = useCallback((patch: Partial<OnboardingDraft>) => {
    saveDraft(patch)
  }, [])
  return [draft, update] as const
}

/** Where a signed-out visitor was heading, so login can take them back. */
const RETURN_KEY = 'pass.returnTo'

export function rememberReturn(path: string): void {
  try {
    window.sessionStorage.setItem(RETURN_KEY, path)
  } catch {
    /* nothing to remember in private mode */
  }
}

/** Reads without clearing — safe to call during render, which may happen twice. */
export function peekReturn(): string | null {
  try {
    return window.sessionStorage.getItem(RETURN_KEY)
  } catch {
    return null
  }
}

export function clearReturn(): void {
  try {
    window.sessionStorage.removeItem(RETURN_KEY)
  } catch {
    /* nothing was stored */
  }
}

/** A step records itself when it is shown, so resuming lands on the right one. */
export function useStep(step: OnboardingStep): void {
  useEffect(() => {
    if (load().step !== step) saveDraft({ step })
  }, [step])
}
